import React, { useState } from "react";
import {
  Layout,
  Maximize2,
  Smartphone,
  Monitor,
  Tablet,
  Layers,
  Award,
  Code,
  Image as ImageIcon,
  FileText,
  HelpCircle,
  ExternalLink,
  Copy,
  Check,
  Eye,
  BookOpen,
  ArrowUpRight,
  Clock,
  Sparkles,
  TrendingUp,
  X
} from "lucide-react";
import { AdLayoutSpec, AD_LAYOUT_SPECS, getAdLayoutSpec } from "../utils/adLayoutSpecs";
import type { Advertisement } from "../types";

export interface AdLayoutBadgeProps {
  placement: string;
  adType?: "image" | "text" | "html" | "video";
  showDimensions?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function AdLayoutBadge({
  placement,
  adType,
  showDimensions = true,
  className = "",
  size = "md"
}: AdLayoutBadgeProps) {
  const spec = getAdLayoutSpec(placement);

  const getFormatIcon = () => {
    if (adType === "html") return <Code size={11} className="text-purple-600" />;
    if (adType === "text") return <FileText size={11} className="text-amber-600" />;
    return <ImageIcon size={11} className="text-blue-600" />;
  };

  if (size === "sm") {
    return (
      <span
        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${spec.badgeColor.bg} ${spec.badgeColor.text} ${spec.badgeColor.border} ${className}`}
        title={`${spec.name} (${spec.dimensions.desktop}) - ${spec.iabStandard}`}
      >
        <Layout size={10} />
        <span>{spec.shortLabel}</span>
        {showDimensions && (
          <span className="opacity-75 font-mono text-[10px] font-normal">
            ({spec.dimensions.desktop.split(" ")[0]}×{spec.dimensions.desktop.split(" ")[2]})
          </span>
        )}
      </span>
    );
  }

  return (
    <div
      className={`inline-flex flex-col gap-0.5 px-2.5 py-1 rounded-md border ${spec.badgeColor.bg} ${spec.badgeColor.border} ${className}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="flex items-center justify-center p-0.5 rounded bg-white/70 shadow-2xs">
          <Layout size={12} className={spec.badgeColor.text} />
        </span>
        <span className={`text-xs font-bold ${spec.badgeColor.text}`}>{spec.shortLabel}</span>
        {adType && <span title={`Format: ${adType}`}>{getFormatIcon()}</span>}
      </div>
      {showDimensions && (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-600 font-mono">
          <span className="font-semibold">{spec.dimensions.desktop}</span>
          <span className="text-slate-400">•</span>
          <span className="text-slate-500">{spec.aspectRatio.split(" ")[0]} {spec.aspectRatio.split(" ")[1]}</span>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   ACTUAL AD RENDER COMPONENT (High-Fidelity Preview)
   ========================================================= */

export interface AdActualRenderProps {
  placement: string;
  adType?: "image" | "text" | "html" | "video";
  name?: string;
  headline?: string;
  sponsorName?: string;
  bodyText?: string;
  imageUrl?: string;
  ctaText?: string;
  destinationUrl?: string;
  htmlContent?: string;
  altText?: string;
  viewport?: "desktop" | "tablet" | "mobile";
  className?: string;
  interactive?: boolean;
}

export function AdActualRender({
  placement,
  adType = "image",
  name = "Commercial Campaign",
  headline,
  sponsorName,
  bodyText,
  imageUrl,
  ctaText = "Learn More",
  destinationUrl = "https://example.com",
  htmlContent,
  altText,
  viewport = "desktop",
  className = "",
  interactive = true
}: AdActualRenderProps) {
  const spec = getAdLayoutSpec(placement);
  const [clicked, setClicked] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (!interactive) return;
    e.preventDefault();
    setClicked(true);
    setTimeout(() => setClicked(false), 2000);
  };

  const isNativeCard = spec.shape === "native-card" || adType === "text";
  const isBoxMPU = spec.shape === "box-mpu";
  const isSkyscraper = spec.shape === "vertical-skyscraper";

  // Case 1: Custom HTML
  if (adType === "html" && htmlContent) {
    return (
      <div className={`ad-actual-box w-full bg-white border border-slate-200 rounded-lg overflow-hidden shadow-xs ${className}`}>
        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-900 text-white text-[10px] uppercase font-bold tracking-wider">
          <div className="flex items-center gap-1.5">
            <Code size={11} className="text-purple-400" />
            <span>Sponsored Interactive Embed</span>
            {sponsorName && <span className="text-slate-400">• {sponsorName}</span>}
          </div>
          <span className="font-mono text-slate-400">{spec.shortLabel}</span>
        </div>
        <div
          className="p-3 overflow-x-auto min-h-[90px] flex items-center justify-center"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
        {clicked && (
          <div className="bg-emerald-50 text-emerald-800 text-[11px] font-semibold px-3 py-1 text-center border-t border-emerald-200">
            ✓ Preview Click Captured → Destination: {destinationUrl}
          </div>
        )}
      </div>
    );
  }

  // Case 2: Native Text / Sponsored Post Card
  if (isNativeCard || (!imageUrl && (headline || bodyText))) {
    return (
      <div className={`ad-actual-box w-full bg-white border border-amber-200 rounded-lg overflow-hidden shadow-xs hover:border-amber-400 transition-all ${className}`}>
        {/* Disclosure Bar */}
        <div className="flex items-center justify-between px-3.5 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200/80 text-[10px] font-bold text-amber-900">
          <div className="flex items-center gap-1.5">
            <Award size={12} className="text-amber-600" />
            <span className="tracking-wider uppercase">SPONSORED INTELLIGENCE</span>
            {sponsorName && (
              <span className="font-normal text-amber-800">
                • Presented by <strong>{sponsorName}</strong>
              </span>
            )}
          </div>
          <span className="font-mono text-[9px] text-amber-700 bg-amber-100/80 px-1.5 py-0.5 rounded">
            {spec.shortLabel}
          </span>
        </div>

        {/* Body Content */}
        <div
          onClick={handleClick}
          className={`p-4 ${interactive ? "cursor-pointer group" : ""} flex flex-col justify-between`}
        >
          {imageUrl && (
            <div className="w-full h-36 mb-3 overflow-hidden rounded bg-slate-100">
              <img
                src={imageUrl}
                alt={altText || headline || name}
                className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
              />
            </div>
          )}

          <div>
            <h4 className="font-serif font-bold text-slate-900 text-sm leading-snug group-hover:text-amber-800 transition-colors">
              {headline || name || "Strategic Growth & Enterprise Innovation in 2026"}
            </h4>
            <p className="text-xs text-slate-600 leading-relaxed mt-1.5 line-clamp-3">
              {bodyText ||
                "Discover how next-generation digital frameworks and regional capital investments are reshaping institutional workflows and corporate performance across Southern Africa."}
            </p>
          </div>

          <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-100">
            <span className="text-[11px] text-slate-500 font-medium">
              {sponsorName ? `Official partner ${sponsorName}` : "Commercial Partner"}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-amber-700 group-hover:bg-amber-800 px-3 py-1 rounded transition-colors shadow-2xs">
              <span>{ctaText || "Explore Details"}</span>
              <ArrowUpRight size={13} />
            </span>
          </div>

          {clicked && (
            <div className="mt-2 text-center text-[10px] text-emerald-700 font-bold bg-emerald-50 py-1 rounded">
              ✓ Destination URL: {destinationUrl}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Case 3: Display Image Banner / Billboard / MPU Box / Skyscraper
  return (
    <div
      className={`ad-actual-box w-full bg-white border border-slate-200 rounded-lg overflow-hidden shadow-xs ${
        isBoxMPU ? "max-w-[320px] mx-auto" : isSkyscraper ? "max-w-[180px] mx-auto" : ""
      } ${className}`}
    >
      {/* Header Bar */}
      <div className="flex items-center justify-between px-3 py-1 bg-slate-900 text-white text-[10px] uppercase font-bold tracking-wider">
        <div className="flex items-center gap-1.5">
          <Award size={11} className="text-amber-400" />
          <span>ADVERTISEMENT</span>
          {sponsorName && <span className="text-slate-400 font-normal">| {sponsorName}</span>}
        </div>
        <span className="font-mono text-[9px] text-slate-400">
          {viewport === "mobile"
            ? spec.dimensions.mobile
            : viewport === "tablet"
            ? spec.dimensions.tablet || spec.dimensions.desktop
            : spec.dimensions.desktop}
        </span>
      </div>

      {/* Banner Graphic Area */}
      <div
        onClick={handleClick}
        className={`relative w-full bg-slate-950 overflow-hidden ${interactive ? "cursor-pointer group" : ""} ${
          spec.shape === "horizontal-billboard"
            ? "h-44 sm:h-52"
            : spec.shape === "hero-lead"
            ? "h-48 sm:h-64"
            : spec.shape === "box-mpu"
            ? "h-64"
            : spec.shape === "vertical-skyscraper"
            ? "h-96"
            : "h-28 sm:h-36"
        }`}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={altText || headline || name}
            className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
          />
        ) : (
          /* Fallback Visual Banner if no image supplied yet */
          <div className="w-full h-full flex flex-col justify-between p-5 bg-gradient-to-br from-slate-900 via-slate-800 to-amber-950 text-white">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400 bg-amber-950/60 border border-amber-600/40 px-2 py-0.5 rounded">
                {sponsorName || "PREMIUM BRAND PARTNER"}
              </span>
              <span className="text-[10px] font-mono text-slate-400">{spec.iabStandard}</span>
            </div>

            <div className="my-auto py-2">
              <h3 className="text-base sm:text-lg font-serif font-bold text-white leading-tight drop-shadow-xs">
                {headline || name || "Powering Enterprise Innovation Across Africa"}
              </h3>
              <p className="text-xs text-slate-300 mt-1 line-clamp-2">
                {bodyText || "Accelerate your institutional capability with leading industry solutions."}
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <span className="text-[10px] text-slate-400 font-mono">
                {destinationUrl.replace(/^https?:\/\//, "")}
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-950 bg-amber-400 group-hover:bg-amber-300 px-3 py-1 rounded transition-colors">
                <span>{ctaText || "Learn More"}</span>
                <ArrowUpRight size={12} />
              </span>
            </div>
          </div>
        )}

        {/* Hover Overlay with CTA & Click Test */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-3">
          <div className="bg-slate-900/90 backdrop-blur-xs text-white px-3.5 py-2 rounded-lg text-xs font-bold flex items-center gap-2 border border-slate-700 shadow-lg">
            <span>Click to test link</span>
            <ExternalLink size={13} className="text-amber-400" />
          </div>
        </div>
      </div>

      {clicked && (
        <div className="bg-emerald-50 text-emerald-800 text-[11px] font-semibold px-3 py-1.5 text-center border-t border-emerald-200">
          ✓ Preview Click Captured → Destination: {destinationUrl}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   SAMPLE EDITORIAL CONTEXT OVERLAY COMPONENT
   Surrounds the ad with realistic magazine content
   ========================================================= */

export interface AdEditorialContextOverlayProps {
  placement: string;
  adContent: React.ReactNode;
  viewport?: "desktop" | "tablet" | "mobile";
}

export function AdEditorialContextOverlay({
  placement,
  adContent,
  viewport = "desktop"
}: AdEditorialContextOverlayProps) {
  const spec = getAdLayoutSpec(placement);

  // Context Template 1: Masthead / Top Billboard (Homepage Top, Category Header)
  if (placement === "homepage_top" || placement === "homepage_hero" || spec.shape === "horizontal-billboard") {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-inner text-slate-800 font-sans">
        {/* Mock Top Brand Bar */}
        <div className="bg-slate-950 text-slate-300 px-4 py-1.5 text-[10px] flex items-center justify-between border-b border-slate-800 font-mono">
          <div className="flex items-center gap-2">
            <span className="font-bold text-white font-serif">ZIMSMART</span>
            <span>•</span>
            <span>HARARE, ZW</span>
            <span>•</span>
            <span>SUN, AUG 16, 2026</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-amber-400">● LIVE DISPATCH</span>
            <span className="hidden sm:inline text-slate-400">EDITION #412</span>
          </div>
        </div>

        {/* Mock Main Navigation */}
        <div className="bg-white px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
          <div className="font-serif font-black text-xl tracking-tight text-slate-950">
            ZIMSMART <span className="text-amber-700 text-xs font-sans font-bold uppercase tracking-widest ml-1">MAGAZINE</span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs font-bold text-slate-700">
            <span className="text-amber-800 border-b-2 border-amber-800 pb-0.5">Top Stories</span>
            <span>Economy</span>
            <span>Fintech</span>
            <span>Mining</span>
            <span>Technology</span>
          </div>
        </div>

        {/* Breaking Ticker */}
        <div className="bg-amber-100/70 border-b border-amber-200 px-4 py-1.5 text-[11px] flex items-center gap-2">
          <span className="font-bold text-amber-950 uppercase text-[10px] bg-amber-200 px-1.5 py-0.5 rounded">
            BREAKING
          </span>
          <span className="text-amber-900 truncate">
            Reserve Bank of Zimbabwe expands digital gold-backed tokens liquidity window for commercial banks...
          </span>
        </div>

        {/* IN-SITU ADVERTISEMENT SLOT */}
        <div className="p-4 bg-slate-100/80 border-b border-slate-200">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center mb-1.5 flex items-center justify-center gap-1.5">
            <Layout size={11} className="text-amber-600" />
            <span>Editorial Layout Position: Masthead Above-The-Fold Slot</span>
          </div>
          {adContent}
        </div>

        {/* Editorial Content Below Ad: Lead Cover Story */}
        <div className="p-5 bg-white space-y-4">
          <div className="flex items-center gap-2 text-xs font-bold text-amber-800 uppercase tracking-wider">
            <Award size={13} />
            <span>Lead Investigation & Cover Dispatch</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <h2 className="font-serif font-bold text-slate-950 text-xl leading-tight">
                The New Silicon Highveld: How Harare Tech Hubs Are Powering SADC Cross-Border Settlements
              </h2>
              <p className="text-xs text-slate-600 leading-relaxed">
                Over the past 24 months, domestic fintech engineers and banking consortiums have deployed interoperable settlement pipelines, driving a 340% surge in real-time transaction processing.
              </p>
              <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono pt-1">
                <span>By Editorial Desk</span>
                <span>•</span>
                <span>4 min read</span>
                <span>•</span>
                <span className="text-emerald-700 font-bold">Updated 12m ago</span>
              </div>
            </div>
            <div className="h-28 bg-slate-200 rounded-lg overflow-hidden flex items-center justify-center text-slate-400 text-xs font-bold">
              [Featured Article Media]
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Context Template 2: In-Article / Mid-Prose Break (article_middle, article_top, article_bottom)
  if (placement === "article_middle" || placement === "article_top" || placement === "article_bottom") {
    return (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-inner font-sans">
        {/* Mock Article Header */}
        <div className="p-5 border-b border-slate-100 bg-slate-50/60">
          <span className="text-[10px] font-bold text-amber-800 uppercase tracking-widest bg-amber-100 px-2 py-0.5 rounded">
            FINANCE & DIGITAL INFRASTRUCTURE
          </span>
          <h1 className="font-serif font-bold text-slate-950 text-lg sm:text-xl leading-snug mt-2">
            Institutional Capital Allocation in Zimbabwe's Expanding Renewable Energy Grid
          </h1>
          <div className="flex items-center gap-2 text-xs text-slate-500 font-mono mt-2">
            <span className="font-bold text-slate-800">By Admin (Lead Economics Correspondent)</span>
            <span>•</span>
            <span>August 16, 2026</span>
          </div>
        </div>

        {/* Article Prose Paragraph 1 */}
        <div className="p-5 space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
          <p>
            Private equity funds and sovereign wealth allocations across the SADC basin are converging on decentralized solar microgrids and industrial battery storage facilities. Commercial operators report a rapid compression in payback periods as solar hardware tariffs drop.
          </p>

          {/* IN-SITU ADVERTISEMENT SLOT */}
          <div className="my-4 p-3 bg-slate-100/70 border border-slate-200 rounded-lg">
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center mb-1.5 flex items-center justify-center gap-1.5">
              <Layout size={11} className="text-amber-600" />
              <span>In-Prose Interstitial Placement ({spec.shortLabel})</span>
            </div>
            {adContent}
          </div>

          <p>
            “The convergence of digital metering, mobile money billing, and utility-scale solar arrays has altered the foundational economics of off-grid manufacturing,” noted one chief infrastructure analyst during yesterday's symposium in Victoria Falls.
          </p>

          <div className="p-3 bg-amber-50/60 border-l-4 border-amber-600 text-xs italic text-slate-800 font-serif">
            “Domestic grid resilience is directly correlated with modern private sector capitalization and intelligent energy distribution.”
          </div>
        </div>
      </div>
    );
  }

  // Context Template 3: Sidebar MPU / Skyscraper (article_sidebar, feed_sidebar)
  if (placement === "article_sidebar" || placement === "feed_sidebar" || spec.shape === "box-mpu" || spec.shape === "vertical-skyscraper") {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden p-4 shadow-inner font-sans">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Main Editorial Column (Left 2/3) */}
          <div className="md:col-span-2 space-y-3 bg-white p-4 rounded-lg border border-slate-200">
            <span className="text-[10px] font-bold uppercase text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">
              MARKETS & AGRIBUSINESS
            </span>
            <h2 className="font-serif font-bold text-base text-slate-900 leading-snug">
              Horticultural Export Growth Hits 5-Year High with Direct Air Freight Links
            </h2>
            <p className="text-xs text-slate-600 leading-relaxed">
              Zimbabwean blueberry and avocado growers have expanded cold-chain logistics through Robert Gabriel Mugabe International Airport, securing continuous daily cargo dispatch to European and Middle Eastern premium retailers.
            </p>
            <p className="text-xs text-slate-600 leading-relaxed">
              Total volume throughput increased by 28% quarter-on-quarter, driven by smart drip irrigation automation and international organic certifications.
            </p>
            <div className="h-24 bg-slate-100 rounded border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">
              [Editorial Market Data Chart]
            </div>
          </div>

          {/* Sticky Sidebar Column (Right 1/3) */}
          <div className="space-y-4">
            <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-2xs">
              <div className="flex items-center gap-1.5 pb-2 mb-2 border-b border-slate-100 text-xs font-bold text-slate-900 uppercase">
                <TrendingUp size={13} className="text-amber-600" />
                <span>Trending Dispatches</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="font-medium text-slate-800 hover:text-amber-800 cursor-pointer">
                  1. Zimplats advances $1.8B green smelting expansion
                </div>
                <div className="font-medium text-slate-800 hover:text-amber-800 cursor-pointer">
                  2. Telecel restructuring draws regional tech consortium
                </div>
              </div>
            </div>

            {/* IN-SITU ADVERTISEMENT SLOT */}
            <div className="p-2 bg-slate-200/60 rounded-lg border border-slate-300">
              <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest text-center mb-1">
                SIDEBAR SPONSOR MPU
              </div>
              {adContent}
            </div>

            <div className="bg-white p-3 rounded-lg border border-slate-200 text-xs space-y-1.5">
              <div className="font-bold text-slate-900">Editor's Newsletter</div>
              <p className="text-[11px] text-slate-500">Get daily executive summaries delivered at 6:00 AM.</p>
              <button className="w-full text-xs font-bold bg-slate-900 text-white py-1 rounded">
                Subscribe Free
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Context Template 4: Sponsored Grid / Feed (homepage_sponsored, category_sponsored)
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden p-5 shadow-inner font-sans space-y-4">
      <div className="flex items-center justify-between pb-2 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Award size={16} className="text-amber-600" />
          <h3 className="font-serif font-bold text-sm text-slate-900 uppercase tracking-wider">
            INDUSTRY SPOTLIGHT & SPONSORED DISPATCHES
          </h3>
        </div>
        <span className="text-[10px] text-slate-500 font-mono">PAID BRAND COLLABORATIONS</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Editorial Story 1 */}
        <div className="bg-white p-3.5 rounded-lg border border-slate-200 space-y-2">
          <span className="text-[10px] font-bold text-sky-800 bg-sky-50 px-1.5 py-0.5 rounded">
            TELECOM & CLOUD
          </span>
          <h4 className="font-serif font-bold text-xs text-slate-900">
            Fibre Optic Rollout Accelerates High-Speed Connectivity to Rural District Centers
          </h4>
          <p className="text-[11px] text-slate-500 line-clamp-2">
            Over 2,400km of high-density trunk lines have been laid along primary commercial corridors.
          </p>
        </div>

        {/* IN-SITU ADVERTISEMENT SLOT (CENTER CARD) */}
        <div className="md:col-span-1">
          {adContent}
        </div>

        {/* Editorial Story 2 */}
        <div className="bg-white p-3.5 rounded-lg border border-slate-200 space-y-2">
          <span className="text-[10px] font-bold text-purple-800 bg-purple-50 px-1.5 py-0.5 rounded">
            MANUFACTURING
          </span>
          <h4 className="font-serif font-bold text-xs text-slate-900">
            Local Pharmaceutical Production Capacity Expands with Automated Tablet Compounding
          </h4>
          <p className="text-[11px] text-slate-500 line-clamp-2">
            Domestic healthcare facilities benefit from shortened replenishment times and reduced import costs.
          </p>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   SCHEMATIC & LIVE PREVIEW COMPONENT
   ========================================================= */

export interface AdLayoutSchematicProps {
  placement: string;
  adType?: "image" | "text" | "html" | "video";
  name?: string;
  headline?: string;
  sponsorName?: string;
  bodyText?: string;
  imageUrl?: string;
  ctaText?: string;
  destinationUrl?: string;
  htmlContent?: string;
  altText?: string;
  className?: string;
  interactive?: boolean;
  viewport?: "desktop" | "tablet" | "mobile";
  showSampleContent?: boolean;
  viewMode?: "actual" | "wireframe";
}

export function AdLayoutSchematic({
  placement,
  adType = "image",
  name,
  headline,
  sponsorName,
  bodyText,
  imageUrl,
  ctaText = "Learn More",
  destinationUrl,
  htmlContent,
  altText,
  className = "",
  interactive = false,
  viewport = "desktop",
  showSampleContent = false,
  viewMode = "actual"
}: AdLayoutSchematicProps) {
  const spec = getAdLayoutSpec(placement);

  const renderedAd = (
    <AdActualRender
      placement={placement}
      adType={adType}
      name={name}
      headline={headline}
      sponsorName={sponsorName}
      bodyText={bodyText}
      imageUrl={imageUrl}
      ctaText={ctaText}
      destinationUrl={destinationUrl}
      htmlContent={htmlContent}
      altText={altText}
      viewport={viewport}
      interactive={interactive}
    />
  );

  if (showSampleContent) {
    return (
      <div className={`ad-layout-schematic-wrap ${className}`}>
        <AdEditorialContextOverlay
          placement={placement}
          adContent={renderedAd}
          viewport={viewport}
        />
      </div>
    );
  }

  // Isolated Ad Unit Mode
  if (viewMode === "actual") {
    return (
      <div className={`ad-layout-schematic-wrap ${className}`}>
        {/* Spec Meta Bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-slate-900 text-white rounded-t-lg border border-slate-800 text-xs">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 font-bold text-amber-400">
              <Layout size={14} />
              <span>{spec.layoutCategory}</span>
            </span>
            <span className="text-slate-500">|</span>
            <span className="font-mono text-slate-300">{spec.iabStandard}</span>
          </div>

          <div className="flex items-center gap-3 text-[11px] text-slate-400 font-mono">
            <span className="flex items-center gap-1">
              <Monitor size={12} className="text-sky-400" />
              <strong className="text-white">{spec.dimensions.desktop}</strong>
            </span>
            <span className="flex items-center gap-1">
              <Smartphone size={12} className="text-emerald-400" />
              <span>{spec.dimensions.mobile}</span>
            </span>
          </div>
        </div>

        {/* Visual Rendered Canvas */}
        <div className="p-4 bg-slate-100 border-x border-b border-slate-300 rounded-b-lg">
          <div className="mx-auto max-w-full">
            {renderedAd}
          </div>
          <p className="text-[11px] text-slate-500 mt-2.5 text-center max-w-md mx-auto">
            {spec.description}
          </p>
        </div>
      </div>
    );
  }

  // Wireframe Blueprint Mode
  const getShapeStyle = () => {
    switch (spec.shape) {
      case "horizontal-billboard":
        return "h-28 w-full";
      case "hero-lead":
        return "h-36 w-full";
      case "horizontal-leaderboard":
        return "h-20 w-full";
      case "horizontal-inline":
        return "h-20 w-full";
      case "box-mpu":
        return "h-44 w-52 mx-auto";
      case "vertical-skyscraper":
        return "h-56 w-36 mx-auto";
      case "native-card":
        return "h-40 w-full";
      case "newsletter-bar":
        return "h-24 w-full";
      default:
        return "h-24 w-full";
    }
  };

  return (
    <div className={`ad-layout-schematic-wrap ${className}`}>
      {/* Spec Meta Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-slate-900 text-white rounded-t-lg border border-slate-800 text-xs">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 font-bold text-amber-400">
            <Layout size={14} />
            <span>{spec.layoutCategory}</span>
          </span>
          <span className="text-slate-500">|</span>
          <span className="font-mono text-slate-300">{spec.iabStandard}</span>
        </div>

        <div className="flex items-center gap-3 text-[11px] text-slate-400 font-mono">
          <span className="flex items-center gap-1">
            <Monitor size={12} className="text-sky-400" />
            <strong className="text-white">{spec.dimensions.desktop}</strong>
          </span>
          <span className="flex items-center gap-1">
            <Smartphone size={12} className="text-emerald-400" />
            <span>{spec.dimensions.mobile}</span>
          </span>
        </div>
      </div>

      {/* Visual Canvas Wireframe */}
      <div className="p-4 bg-slate-100 border-x border-b border-slate-300 rounded-b-lg flex flex-col items-center justify-center">
        <div
          className={`relative border-2 border-dashed border-slate-400 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col justify-between transition-all ${getShapeStyle()}`}
        >
          <div className="absolute top-1.5 left-2 z-10 flex items-center gap-1.5 bg-slate-900/80 backdrop-blur-xs text-white text-[10px] font-mono px-2 py-0.5 rounded">
            <span className="font-bold text-amber-300">{spec.dimensions.desktop}</span>
            <span className="text-slate-400">({spec.aspectRatio})</span>
          </div>

          <div className="absolute top-1.5 right-2 z-10 text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-white/90 px-1.5 py-0.5 rounded border border-slate-200">
            {adType === "html" ? "HTML Slot" : adType === "text" ? "Native Sponsor" : "Image Banner"}
          </div>

          <div className="h-full w-full flex items-center justify-center p-3 pt-6">
            <div className="flex flex-col items-center justify-center text-center text-slate-400">
              <ImageIcon size={20} className="mb-1 text-slate-400" />
              <span className="text-xs font-semibold text-slate-600">{spec.name}</span>
              <span className="text-[10px] text-slate-400 font-mono mt-0.5">
                Aspect Ratio: {spec.aspectRatio}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border-t border-slate-200 px-2 py-1 flex items-center justify-between text-[10px] text-slate-500 font-medium">
            <span>Location: <strong className="text-slate-800">{spec.name}</strong></span>
            <span className="text-emerald-700 font-mono font-semibold">{spec.responsiveBehavior.slice(0, 40)}...</span>
          </div>
        </div>

        <p className="text-[11px] text-slate-500 mt-2.5 text-center max-w-md">
          {spec.description}
        </p>
      </div>
    </div>
  );
}

/* =========================================================
   COMPREHENSIVE LAYOUT PREVIEW MODAL
   ========================================================= */

export interface AdLayoutPreviewModalProps {
  ad: Advertisement | null;
  onClose: () => void;
  onEdit?: (ad: Advertisement) => void;
  onCopySnippet?: (ad: Advertisement) => void;
}

export function AdLayoutPreviewModal({
  ad,
  onClose,
  onEdit,
  onCopySnippet
}: AdLayoutPreviewModalProps) {
  if (!ad) return null;

  const [viewport, setViewport] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [showSampleContent, setShowSampleContent] = useState(true);
  const [viewMode, setViewMode] = useState<"actual" | "wireframe">("actual");
  const [copied, setCopied] = useState(false);

  const spec = getAdLayoutSpec(ad.placement);

  const handleCopy = () => {
    if (onCopySnippet) {
      onCopySnippet(ad);
    } else {
      const snippet = `<!-- ZimSmart Ad Slot: ${ad.name} (${spec.shortLabel} - ${spec.dimensions.desktop}) -->
<div class="zimsmart-ad-slot" data-ad-id="${ad.id}" data-placement="${ad.placement}" data-ad-type="${ad.ad_type || 'image'}">
  <a href="${ad.destination_url}" target="_blank" rel="noopener sponsored" style="display:block;text-decoration:none;">
    ${
      ad.image_url
        ? `<img src="${ad.image_url}" alt="${ad.alt_text || ad.name}" style="max-width:100%;height:auto;border-radius:4px;" />`
        : `<div style="padding:16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;">
      <small style="color:#b45309;font-weight:700;text-transform:uppercase;">Sponsored by ${ad.sponsor_name || 'Partner'}</small>
      <h4 style="margin:4px 0 8px;font-weight:700;color:#0f172a;">${ad.headline || ad.name}</h4>
      <p style="margin:0 0 10px;font-size:13px;color:#475569;">${ad.body_text || ''}</p>
      <span style="display:inline-block;padding:6px 12px;background:#b45309;color:#fff;font-size:12px;font-weight:700;border-radius:4px;">${ad.cta_text || 'Learn More'}</span>
    </div>`
    }
  </a>
</div>`;
      navigator.clipboard.writeText(snippet);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mag-modal-backdrop" onClick={onClose}>
      <div
        className="mag-modal-card max-w-4xl max-h-[94vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <button
          type="button"
          onClick={onClose}
          className="mag-modal-close-btn"
          aria-label="Close modal"
        >
          <X size={18} />
        </button>

        <div className="p-6">
          {/* Header Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-200">
            <div>
              <div className="flex items-center gap-2">
                <span className="p-1.5 bg-amber-100 text-amber-800 rounded-lg">
                  <Layout size={18} />
                </span>
                <h2 className="text-xl font-serif font-bold text-slate-900">
                  Live Ad & Layout Preview
                </h2>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Campaign: <strong className="text-slate-800">{ad.name}</strong> • Placement:{" "}
                <strong className="text-slate-800">{spec.name}</strong> ({spec.iabStandard})
              </p>
            </div>

            {/* Viewport Switcher */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              <button
                type="button"
                onClick={() => setViewport("desktop")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-all ${
                  viewport === "desktop"
                    ? "bg-white text-slate-900 shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Monitor size={14} />
                <span>Desktop (1200px)</span>
              </button>
              <button
                type="button"
                onClick={() => setViewport("tablet")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-all ${
                  viewport === "tablet"
                    ? "bg-white text-slate-900 shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Tablet size={14} />
                <span>Tablet (768px)</span>
              </button>
              <button
                type="button"
                onClick={() => setViewport("mobile")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-all ${
                  viewport === "mobile"
                    ? "bg-white text-slate-900 shadow-xs font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Smartphone size={14} />
                <span>Mobile (375px)</span>
              </button>
            </div>
          </div>

          {/* Interactive Controls Bar: Sample Content Overlay Toggle & Mode Selector */}
          <div className="flex flex-wrap items-center justify-between gap-3 my-4 p-3 bg-slate-100/90 rounded-lg border border-slate-200">
            {/* Toggle: Sample Editorial Content Overlay */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showSampleContent}
                onChange={(e) => setShowSampleContent(e.target.checked)}
                className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500"
              />
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                <BookOpen size={14} className="text-amber-700" />
                <span>Show Sample Editorial Content Overlay</span>
              </div>
              <span className="text-[11px] text-slate-500 hidden sm:inline font-normal">
                (Simulates realistic in-situ magazine page layout surrounding the ad)
              </span>
            </label>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white p-0.5 rounded border border-slate-200 text-xs">
              <button
                type="button"
                onClick={() => setViewMode("actual")}
                className={`px-2.5 py-1 rounded font-semibold transition-all ${
                  viewMode === "actual"
                    ? "bg-slate-900 text-white font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Actual Rendered Creative
              </button>
              <button
                type="button"
                onClick={() => setViewMode("wireframe")}
                className={`px-2.5 py-1 rounded font-semibold transition-all ${
                  viewMode === "wireframe"
                    ? "bg-slate-900 text-white font-bold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Wireframe Blueprint
              </button>
            </div>
          </div>

          {/* Responsive Preview Canvas Container with Device Frame */}
          <div className="bg-slate-950 p-4 sm:p-6 rounded-xl border border-slate-800 overflow-x-auto flex justify-center shadow-inner">
            <div
              style={{
                width:
                  viewport === "mobile"
                    ? "375px"
                    : viewport === "tablet"
                    ? "768px"
                    : "100%",
                maxWidth: "100%"
              }}
              className="transition-all duration-300"
            >
              {/* Device Frame Header */}
              <div className="flex items-center justify-between pb-2 mb-2 text-[10px] font-mono text-slate-400 border-b border-slate-800">
                <span className="flex items-center gap-1 text-slate-300">
                  {viewport === "mobile" ? <Smartphone size={11} /> : viewport === "tablet" ? <Tablet size={11} /> : <Monitor size={11} />}
                  <span>
                    Viewport: {viewport.toUpperCase()} ({viewport === "mobile" ? "375px" : viewport === "tablet" ? "768px" : "1200px"})
                  </span>
                </span>
                <span className="text-amber-400 font-semibold">
                  Target Slot Dimension:{" "}
                  {viewport === "mobile"
                    ? spec.dimensions.mobile
                    : viewport === "tablet"
                    ? spec.dimensions.tablet || spec.dimensions.desktop
                    : spec.dimensions.desktop}
                </span>
              </div>

              {/* Schematic / Actual Render */}
              <AdLayoutSchematic
                placement={ad.placement}
                adType={ad.ad_type || (ad.html_content ? "html" : ad.headline ? "text" : "image")}
                name={ad.name}
                headline={ad.headline || undefined}
                sponsorName={ad.sponsor_name || undefined}
                bodyText={ad.body_text || undefined}
                imageUrl={ad.image_url || undefined}
                ctaText={ad.cta_text || undefined}
                destinationUrl={ad.destination_url}
                htmlContent={ad.html_content || undefined}
                altText={ad.alt_text || undefined}
                interactive={true}
                viewport={viewport}
                showSampleContent={showSampleContent}
                viewMode={viewMode}
              />
            </div>
          </div>

          {/* Specification Breakdown Matrix */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 mt-4 bg-slate-50 border border-slate-200 rounded-lg text-xs">
            <div>
              <div className="font-bold text-slate-500 uppercase text-[10px]">IAB Publishing Spec</div>
              <div className="font-semibold text-slate-900 mt-0.5">{spec.iabStandard}</div>
            </div>
            <div>
              <div className="font-bold text-slate-500 uppercase text-[10px]">Active Aspect Ratio</div>
              <div className="font-semibold text-slate-900 mt-0.5">{spec.aspectRatio}</div>
            </div>
            <div>
              <div className="font-bold text-slate-500 uppercase text-[10px]">Active Viewport Size</div>
              <div className="font-mono font-bold text-amber-800 mt-0.5">
                {viewport === "mobile"
                  ? spec.dimensions.mobile
                  : viewport === "tablet"
                  ? spec.dimensions.tablet || spec.dimensions.desktop
                  : spec.dimensions.desktop}
              </div>
            </div>
            <div>
              <div className="font-bold text-slate-500 uppercase text-[10px]">Destination Link</div>
              <div className="font-mono text-slate-700 truncate mt-0.5" title={ad.destination_url}>
                {ad.destination_url}
              </div>
            </div>
          </div>

          {/* Bottom Action Footer */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-5 mt-5 border-t border-slate-200">
            <button
              type="button"
              onClick={handleCopy}
              className="mag-admin-secondary-btn flex items-center gap-1.5"
            >
              {copied ? <Check size={15} className="text-emerald-700" /> : <Copy size={15} />}
              <span>{copied ? "Copied Embed HTML Snippet!" : "Copy Embed HTML Snippet"}</span>
            </button>

            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onEdit(ad);
                  }}
                  className="px-4 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Edit Campaign Creative
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="mag-admin-primary-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   LAYOUT GUIDE MATRIX MODAL
   ========================================================= */

export interface AdLayoutGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlacement?: string;
  onSelectPlacement?: (placement: string) => void;
}

export function AdLayoutGuideModal({
  isOpen,
  onClose,
  selectedPlacement,
  onSelectPlacement
}: AdLayoutGuideModalProps) {
  if (!isOpen) return null;

  return (
    <div className="mag-modal-backdrop" onClick={onClose}>
      <div
        className="mag-modal-card max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-20">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-amber-100 text-amber-800 rounded-lg">
                <Layout size={18} />
              </div>
              <h2 className="text-xl font-serif font-bold text-slate-900">
                Commercial Ad Layouts & Dimensional Matrix
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Standard IAB publishing layouts, aspect ratios, responsive breakpoints, and creative requirements.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-2 rounded-lg hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(AD_LAYOUT_SPECS).map(([key, spec]) => {
              const isSelected = selectedPlacement === key;
              return (
                <div
                  key={key}
                  className={`p-4 border rounded-xl transition-all ${
                    isSelected
                      ? "border-amber-600 ring-2 ring-amber-500/20 bg-amber-50/20"
                      : "border-slate-200 hover:border-slate-300 bg-white shadow-2xs"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded border ${spec.badgeColor.bg} ${spec.badgeColor.text} ${spec.badgeColor.border}`}
                        >
                          {spec.layoutCategory}
                        </span>
                        {isSelected && (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.2 rounded">
                            CURRENTLY CHOSEN
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-slate-900 text-sm mt-1">{spec.name}</h3>
                    </div>

                    {onSelectPlacement && (
                      <button
                        type="button"
                        onClick={() => {
                          onSelectPlacement(key);
                          onClose();
                        }}
                        className={`text-xs font-bold px-2.5 py-1 rounded transition-colors ${
                          isSelected
                            ? "bg-amber-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        {isSelected ? "Selected" : "Select Layout"}
                      </button>
                    )}
                  </div>

                  {/* Dimension Grid */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 my-3 grid grid-cols-2 gap-2 text-xs font-mono">
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase block font-sans">
                        Desktop Dimension:
                      </span>
                      <strong className="text-slate-900">{spec.dimensions.desktop}</strong>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase block font-sans">
                        Mobile Dimension:
                      </span>
                      <strong className="text-slate-900">{spec.dimensions.mobile}</strong>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase block font-sans">
                        Aspect Ratio:
                      </span>
                      <span className="text-amber-800 font-semibold">{spec.aspectRatio}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 uppercase block font-sans">
                        IAB Standard:
                      </span>
                      <span className="text-slate-700">{spec.iabStandard.split(" ")[0]} {spec.iabStandard.split(" ")[1]}</span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    {spec.description}
                  </p>

                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                    <span>Supported: <strong>{spec.compatibleFormats.join(", ").toUpperCase()}</strong></span>
                    <span className="text-slate-400 font-mono">key: {spec.placement}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 text-white font-bold text-xs rounded hover:bg-slate-900"
          >
            Close Layout Guide
          </button>
        </div>
      </div>
    </div>
  );
}
